import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Message, Modal } from 'rsuite';
import { ResponsiveSplitLayout } from '@urbantxt/layout';
import { Loader } from '@urbantxt/loader';
import UnsavedChangesModal from 'components/UnsavedChangesModal/UnsavedChangesModal';
import useIsMobile from 'hooks/useIsMobile';
import useMatchMedia from 'hooks/useMatchMedia';
import { getBagelAiFlow, testBagelAiFlow } from '../../../api/bagel-ai-flow';
import {
  listBagelPrompts,
  updateBagelPrompt,
} from '../../../api/bagel-prompts';
import { BagelPrompt } from '../../../types';
import type {
  BagelAiFlowMap,
  BagelAiFlowNode,
  BagelAiFlowRoute,
  BagelFlowTestResult,
} from '../../types/aiFlow';
import {
  computeAiFlowRankLayout,
  sortNodesByFlowOrder,
} from '../../utils/aiFlowRankLayout';
import AiFlowCanvas from './AiFlowCanvas';
import AiFlowMobileList from './AiFlowMobileList';
import AiFlowTestBox from './AiFlowTestBox';
import NodeInfoPanel from './NodeInfoPanel';
import PromptEditorPanel, { PromptEditorPlaceholder } from './PromptEditorPanel';
import styles from './AiFlowTab.module.scss';

type FlowPane = 'flow' | 'editor';

const PANE_OPTIONS = [
  { label: 'Flow', value: 'flow' as const },
  { label: 'Editor', value: 'editor' as const },
];

export default function AiFlowTab({ readOnly = false }: { readOnly?: boolean }) {
  const { isMobile } = useIsMobile();
  const isWide = useMatchMedia('(min-width: 1280px)');

  const [flow, setFlow] = useState<BagelAiFlowMap | null>(null);
  const [prompts, setPrompts] = useState<BagelPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedNode, setSelectedNode] = useState<BagelAiFlowNode | null>(null);
  const [editorDirty, setEditorDirty] = useState(false);
  const [pendingNode, setPendingNode] = useState<BagelAiFlowNode | null>(null);
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [narrowPane, setNarrowPane] = useState<FlowPane>('flow');

  const [testText, setTestText] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<BagelFlowTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [highlightedPath, setHighlightedPath] = useState<string[]>([]);

  const load = useCallback(async () => {
    try {
      const [flowData, promptData] = await Promise.all([
        getBagelAiFlow(),
        listBagelPrompts(),
      ]);
      setFlow(flowData);
      setPrompts(promptData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI flow');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const layout = useMemo(() => {
    if (!flow) return new Map();
    return computeAiFlowRankLayout(
      flow.nodes.map(n => n.id),
      flow.edges,
    );
  }, [flow]);

  const orderedNodes = useMemo(() => {
    if (!flow) return [];
    return sortNodesByFlowOrder(
      flow.nodes.map(n => n.id),
      layout,
    ).map(id => flow.nodes.find(n => n.id === id)!);
  }, [flow, layout]);

  const selectedPrompt = useMemo(() => {
    if (!selectedNode?.promptName) return null;
    return prompts.find(p => p.name === selectedNode.promptName) ?? null;
  }, [prompts, selectedNode]);

  const selectedRoutes: BagelAiFlowRoute[] = useMemo(() => {
    if (!flow || !selectedNode) return [];
    const nodeById = new Map(flow.nodes.map(n => [n.id, n]));
    return flow.edges
      .filter(edge => edge.from === selectedNode.id)
      .map(edge => ({
        label: edge.label,
        targetId: edge.to,
        targetTitle: nodeById.get(edge.to)?.title ?? edge.to,
        targetDescription: nodeById.get(edge.to)?.description,
      }));
  }, [flow, selectedNode]);

  const selectNode = useCallback(
    (node: BagelAiFlowNode) => {
      if (editorDirty && selectedNode && selectedNode.id !== node.id) {
        setPendingNode(node);
        return;
      }
      setSelectedNode(node);
      if (isMobile) setMobileEditorOpen(true);
      if (!isWide && !isMobile) setNarrowPane('editor');
    },
    [editorDirty, isMobile, isWide, selectedNode],
  );

  const handleSavePrompt = async (name: string, content: string) => {
    const updated = await updateBagelPrompt(name, content);
    setPrompts(prev => prev.map(p => (p.name === name ? updated : p)));
    setEditorDirty(false);
  };

  const handleRunTest = async () => {
    const trimmed = testText.trim();
    if (!trimmed) return;
    setTestRunning(true);
    setTestError(null);
    try {
      const result = await testBagelAiFlow(trimmed);
      setTestResult(result);
      setHighlightedPath(result.path);
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Test failed');
      setTestResult(null);
      setHighlightedPath([]);
    } finally {
      setTestRunning(false);
    }
  };

  let editorPanel: React.ReactNode;
  if (selectedPrompt && selectedNode) {
    editorPanel = (
      <PromptEditorPanel
        prompt={selectedPrompt}
        node={selectedNode}
        routes={selectedRoutes}
        onSave={handleSavePrompt}
        readOnly={readOnly}
        onDirtyChange={setEditorDirty}
      />
    );
  } else if (selectedNode) {
    editorPanel = <NodeInfoPanel node={selectedNode} routes={selectedRoutes} />;
  } else {
    editorPanel = <PromptEditorPlaceholder message="Select a node to view its details." />;
  }

  const testBox = (
    <AiFlowTestBox
      text={testText}
      onTextChange={setTestText}
      onRun={handleRunTest}
      running={testRunning}
      result={testResult}
      error={testError}
    />
  );

  if (loading) {
    return <Loader content="Loading AI flow…" style={{ padding: 16 }} />;
  }

  if (error || !flow) {
    return (
      <Message type="error" showIcon>
        {error ?? 'Failed to load AI flow'}
      </Message>
    );
  }

  const flowView = isMobile ? (
    <AiFlowMobileList
      nodes={orderedNodes}
      highlightedPath={highlightedPath}
      onNodeClick={selectNode}
    />
  ) : (
    <AiFlowCanvas
      nodes={flow.nodes}
      edges={flow.edges}
      highlightedPath={highlightedPath}
      onNodeClick={selectNode}
    />
  );

  const renderLayout = () => {
    if (isMobile) return flowView;
    if (isWide) {
      return (
        <div className={styles.wideLayout}>
          {flowView}
          <div className={styles.editorCol}>{editorPanel}</div>
        </div>
      );
    }
    return (
      <div className={styles.splitShell}>
        <ResponsiveSplitLayout
          primary={flowView}
          secondary={<div className={styles.editorCol}>{editorPanel}</div>}
          activePane={narrowPane}
          onActivePaneChange={(pane: string) => setNarrowPane(pane as FlowPane)}
          paneOptions={PANE_OPTIONS}
          primaryPaneValue="flow"
          secondaryPaneValue="editor"
          primaryHeader="Flow"
          narrowMediaQuery="(max-width: 1279px)"
        />
      </div>
    );
  };

  return (
    <div className={styles.shell}>
      {testBox}
      {renderLayout()}

      <UnsavedChangesModal
        open={pendingNode !== null}
        message="You have unsaved prompt edits. Discard them and switch nodes?"
        onStay={() => setPendingNode(null)}
        onDiscard={() => {
          if (pendingNode) {
            setEditorDirty(false);
            setSelectedNode(pendingNode);
            if (isMobile) setMobileEditorOpen(true);
            if (!isWide && !isMobile) setNarrowPane('editor');
          }
          setPendingNode(null);
        }}
      />

      {isMobile ? (
        <Modal
          open={mobileEditorOpen}
          onClose={() => setMobileEditorOpen(false)}
          size="full"
        >
          <Modal.Header>
            <Modal.Title>{selectedNode?.title ?? 'Prompt editor'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{editorPanel}</Modal.Body>
        </Modal>
      ) : null}
    </div>
  );
}
